import React, { useRef, useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './Carousel.css';

interface CarouselItem {
    id: number | string;
    name: string;
    type: 'archived' | 'favorite' | 'group' | 'contact';
    count?: number; // For archived/unread count
    // Icon removed as requested for text-only version
}

interface CarouselProps {
    items: CarouselItem[];
    onSelect: (item: CarouselItem) => void;
    selectedId?: number | string | null;
    /** Display as compact grid with all items visible (no scroll, no count) */
    variant?: 'carousel' | 'grid';
}

export const Carousel: React.FC<CarouselProps> = ({ items, onSelect, selectedId, variant = 'carousel' }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Update arrows visibility on scroll
    const checkArrows = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5); // tolerance
        }
    };

    useEffect(() => {
        checkArrows();
        window.addEventListener('resize', checkArrows);
        return () => window.removeEventListener('resize', checkArrows);
    }, [items]);

    const handleScroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            // Scroll by 2 items (approx 50% of container or fixed width)
            // Since we want "2 em 2", we assume each item takes 50%.
            const scrollAmount = container.clientWidth;
            const newScrollLeft = direction === 'left'
                ? container.scrollLeft - scrollAmount
                : container.scrollLeft + scrollAmount;

            container.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            });

            // Check arrows after scroll animation
            setTimeout(checkArrows, 300);
        }
    };

    // Drag handlers
    const onMouseDown = (e: React.MouseEvent) => {
        if (!scrollRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const onMouseLeave = () => {
        setIsDragging(false);
    };

    const onMouseUp = () => {
        setIsDragging(false);
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2; // scroll-fast
        scrollRef.current.scrollLeft = scrollLeft - walk;
        checkArrows();
    };

    // Touch handlers for mobile
    const onTouchStart = (e: React.TouchEvent) => {
        if (!scrollRef.current) return;
        setIsDragging(true);
        setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    }

    const onTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || !scrollRef.current) return;
        const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        scrollRef.current.scrollLeft = scrollLeft - walk;
        checkArrows();
    }

    const onTouchEnd = () => {
        setIsDragging(false);
    }

    const isGrid = variant === 'grid';

    return (
        <div className={`cweb-carousel ${isGrid ? 'cweb-carousel--grid' : ''}`}>
            {!isGrid && showLeftArrow && (
                <button className="cweb-carousel-arrow cweb-carousel-left" onClick={() => handleScroll('left')}>
                    <FiChevronLeft />
                </button>
            )}

            <div
                className={`cweb-carousel-container ${isGrid ? 'cweb-carousel-container--grid' : ''}`}
                ref={scrollRef}
                onScroll={isGrid ? undefined : checkArrows}
                onMouseDown={isGrid ? undefined : onMouseDown}
                onMouseLeave={isGrid ? undefined : onMouseLeave}
                onMouseUp={isGrid ? undefined : onMouseUp}
                onMouseMove={isGrid ? undefined : onMouseMove}
                onTouchStart={isGrid ? undefined : onTouchStart}
                onTouchMove={isGrid ? undefined : onTouchMove}
                onTouchEnd={isGrid ? undefined : onTouchEnd}
            >
                <div className={`cweb-carousel-track ${isGrid ? 'cweb-carousel-track--grid' : ''}`}>
                    {items.map((item) => (
                        <div
                            key={`${item.type}-${item.id}`}
                            className={`cweb-carousel-item ${selectedId === item.id ? 'cweb-carousel-selected' : ''}`}
                            onClick={() => (!isGrid && isDragging ? undefined : onSelect(item))}
                        >
                            <span className="cweb-carousel-text">{item.name}</span>
                            {!isGrid && item.count !== undefined && item.count > 0 && (
                                <span className="cweb-carousel-badge">{item.count}</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {!isGrid && showRightArrow && (
                <button className="cweb-carousel-arrow cweb-carousel-right" onClick={() => handleScroll('right')}>
                    <FiChevronRight />
                </button>
            )}
        </div>
    );
};
